import { TaskModel } from '@bryntum/gantt';
import moment, { MomentInput } from 'moment';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useQueryClient } from 'react-query';
import { useDispatch } from 'react-redux';
import { useHistory } from 'react-router';
import { ProjectId, TaskId } from 'api/models';
import { Project, ProjectPhase } from 'api/project';
import { TaskAPI } from 'api/tasks';
import { API_DATE_FORMAT } from 'components/atoms/due-date-picker';
import { confirm } from 'components/atoms/modal';
import {
  useProjectPhaseByProjectId,
  useSprintsByProjectId,
} from 'data/projects';
import {
  updateGanttSlackAction,
  updateProjectPhaseDatesAction,
  updateProjectSprintDatesAction,
} from 'data/projects/actions';
import { updateProjectSprints } from 'data/projects/sagas';
import { Task } from 'data/tasks';
import {
  createDependencySagaAction,
  dependencyRemovedAction,
  saveTaskDetailSaga,
  updateTaskTimelineSagaAction,
} from 'data/tasks/actions';
import { useTasksFromStoreRQ } from 'data/tasks/hooks';
import {
  useProjectAsRBACResource,
  useWithAccess,
} from 'hooks/rbac/useWithAccess';
import { useNavigateToTask } from 'hooks/use-navigate-to-task';
import { useTranslate } from 'hooks/use-translation';
import { useGetAlignPhaseDatesSetting } from 'queries/advanced-settings/advanced-settings';
import {
  getProjectDependencyMapKey,
  getProjectDetailQueryKey,
  getProjectTasksQueryKey,
} from 'queries/common';
import {
  useCreatePhaseDependencyMutation,
  useDeletePhaseDependencyMutation,
} from 'queries/project-phase';
import {
  DateShiftUpdates,
  addDays,
  EntityDateShiftUpdate,
  EntityType,
  getChildNodeIds,
  getCurrentDuration,
  getDateShiftsForEntity,
  subtractDays,
} from 'utils/date-shift';
import {
  filterTaskUpdates,
  formatDate,
  GanttType,
  getDueDateForGantt,
  getDueDateFromGantt,
  getExtremeDates,
  getGanttPhaseId,
  getGanttSprintId,
  getGanttTaskId,
  getPhaseList,
  getSprintList,
  getTaskList,
} from '../helpers';

type CreateDependencyEventPayload = {
  source: TaskModel;
  target: TaskModel;
};

type GanttEvent = {
  name: string;
  payload: TaskModel | CreateDependencyEventPayload;
};

export const THRESHOLD_TASKS_NUMBER = 50;

const validNodeNames = ['svg', 'circle', 'BUTTON', 'INPUT', 'IMG'];

export const useGanttEventsConfig = ({
  projectId,
  ganttRef,
  updateDatesDelay,
  setUpdateDatesDelay,
  setPhaseUpdates,
  setTaskUpdates,
  setSprintUpdates,
  setEntity,
  setShowDependencyModal,
  dependencyModalTimeout,
}) => {
  /**
   * Component state used to trigger event handlers
   * since Gantt is caching the event handler
   * due to which data models are cached resulting in unexpected behaviour.
   */
  const [event, setEvent] = useState(null as unknown as GanttEvent);
  const navigateToTask = useNavigateToTask();
  const dispatch = useDispatch();
  const { push } = useHistory();
  const projectPhases = useProjectPhaseByProjectId(projectId);

  const [_taskUpdates, _setTaskUpdates] = useState<DateShiftUpdates | null>();
  const [_entityId, setEntityId] = useState<number | null>();

  let extremeProjectPhaseDates: Date[] = useMemo(() => [], []);

  projectPhases?.forEach((phase) => {
    extremeProjectPhaseDates = getExtremeDates(
      extremeProjectPhaseDates as [Date, Date],
      [new Date(phase.startDate), new Date(phase.dueDate)] as [Date, Date],
    );
  });

  const sprints = useSprintsByProjectId(projectId);
  const tasks = useTasksFromStoreRQ(ProjectId(projectId));
  const queryClient = useQueryClient();
  const translate = useTranslate();

  const taskresizeEndDate = useRef<MomentInput>();

  const alignPhaseDates = useGetAlignPhaseDatesSetting();

  const { mutateAsync: createPhaseDependency } =
    useCreatePhaseDependencyMutation(projectId);

  const { mutateAsync: deletePhaseDependency } =
    useDeletePhaseDependencyMutation(projectId);

  const updateTasks = useCallback(
    (updates: EntityDateShiftUpdate[]) => {
      const project = queryClient.getQueryData<Project>(ProjectId(projectId));
      const queryTasks = queryClient.getQueryData<Task[]>(
        getProjectTasksQueryKey(ProjectId(projectId)),
      );

      if (project && queryTasks) {
        const updatedDependentTasks: { [id: number]: Task } = {};
        const updatedTasks = queryTasks.map((task) => {
          const update = updates.find((u) => u.id === task.taskId);

          if (update != null) {
            const { startDate, dueDate, dependencyFor } = update;
            const _update = {} as Partial<Task>;

            if (startDate) {
              _update.startDate = moment(startDate).format(API_DATE_FORMAT);
            }
            if (dueDate) {
              _update.dueDate = moment(dueDate).format(API_DATE_FORMAT);
            }

            if (dependencyFor) {
              dependencyFor.forEach((_dependencyFor) => {
                const _task = queryTasks.find(
                  ({ taskId }) => taskId === _dependencyFor,
                );

                const _taskUpdate =
                  updatedDependentTasks[`${dependencyFor}`] || _task;

                if (_task) {
                  updatedDependentTasks[_dependencyFor] = {
                    ..._taskUpdate,
                    dependentTasks: [
                      ...(_taskUpdate.dependentTasks || []),
                      task,
                    ],
                  };
                }
              });
            }

            return {
              ...task,
              ..._update,
            };
          }

          return task;
        });

        const finalUpdatedTasks = updatedTasks.map((_task) => ({
          ..._task,
          dependentTasks: (updatedDependentTasks[_task.taskId] ?? _task)
            .dependentTasks,
        }));

        const queryKey = getProjectTasksQueryKey(ProjectId(projectId));

        queryClient.setQueryData<Task[]>(queryKey, finalUpdatedTasks);
      }
    },
    [projectId, queryClient],
  );

  const updateTasksWithNullDates = useCallback(
    (taskUpdates: DateShiftUpdates, entityId, taskStore: any) => {
      if (taskUpdates && taskStore) {
        Object.values(taskUpdates).forEach(async (item) => {
          if (item.id === entityId) {
            return;
          }

          const ganttId = getGanttTaskId(item.id);
          const record = taskStore.getById(ganttId);

          if (
            record.get('isCalculatedStartDate') ||
            record.get('isCalculatedEndDate')
          ) {
            record.set('isCalculatedStartDate', false);
            record.set('isCalculatedEndDate', false);

            const updatedTask = await TaskAPI.updateTask(projectId, item.id, {
              startDate:
                taskUpdates[item.id].startDate ||
                formatDate(record.getStartDate()),
              dueDate:
                taskUpdates[item.id].dueDate || getDueDateFromGantt(record),
            });

            dispatch(saveTaskDetailSaga(updatedTask));

            taskUpdates[item.id] = {
              ...taskUpdates[item.id],
              startDate: formatDate(record.getStartDate()),
              dueDate: getDueDateFromGantt(record),
            };
            updateTasks(Object.values(taskUpdates));
          }
        });
      }
    },
    [dispatch, projectId, updateTasks],
  );

  useEffect(() => {
    if (!updateDatesDelay && _taskUpdates && _entityId && ganttRef) {
      const { taskStore } = ganttRef?.instance;

      updateTasksWithNullDates(_taskUpdates, _entityId, taskStore);
    }
  }, [
    _entityId,
    _taskUpdates,
    ganttRef,
    updateTasksWithNullDates,
    updateDatesDelay,
  ]);

  const updateGanttDates = useCallback(
    (ganttId, currentEntity, startDate, dueDate): Date[] => {
      const {
        taskStore,
        //  project
      } = ganttRef?.instance;
      const record = taskStore.getById(ganttId);

      if (!record) {
        return [];
      }

      const spanDates: Date[] = [
        new Date(startDate || currentEntity.startDate),
        new Date(dueDate || currentEntity.dueDate),
      ];

      record.setStartDate(formatDate(startDate || currentEntity.startDate));
      record.setEndDate(getDueDateForGantt(dueDate || currentEntity.dueDate));

      return spanDates;
    },
    [ganttRef],
  );

  const updateProjectPhases = useCallback(
    (updates: EntityDateShiftUpdate[]) => {
      const project = queryClient.getQueryData<Project>(ProjectId(projectId));

      if (project) {
        const { projectPhases: phases } = project;
        const updatedDependentPhases: { [id: number]: ProjectPhase } = {};
        const updatedPhases = phases.map((phase) => {
          const update = updates.find((u) => u.id === phase.projectPhaseId);

          if (update != null) {
            const { startDate, dueDate, dependencyFor } = update;
            const _update = {} as Partial<ProjectPhase>;

            if (startDate) {
              _update.startDate = moment(startDate).format(API_DATE_FORMAT);
            }
            if (dueDate) {
              _update.dueDate = moment(dueDate).format(API_DATE_FORMAT);
            }
            if (dependencyFor) {
              dependencyFor.forEach((_dependencyFor) => {
                const _phase = phases.find(
                  ({ projectPhaseId }) => projectPhaseId === _dependencyFor,
                );

                const _phaseUpdate =
                  updatedDependentPhases[`${dependencyFor}`] || _phase;

                if (_phase) {
                  updatedDependentPhases[_dependencyFor] = {
                    ..._phaseUpdate,
                    dependentProjectPhases: [
                      ...(_phaseUpdate.dependentProjectPhases || []),
                      phase,
                    ],
                  };
                }
              });
            }

            return {
              ...phase,
              ..._update,
            };
          }

          return phase;
        });

        const finalUpdatedPhases = updatedPhases.map((_phase) => ({
          ..._phase,
          dependentProjectPhases: (
            updatedDependentPhases[_phase.projectPhaseId] ?? _phase
          ).dependentProjectPhases,
        }));

        const queryKey = getProjectDetailQueryKey(ProjectId(projectId));

        queryClient.setQueryData<Project>(queryKey, () => ({
          ...project,
          projectPhases: finalUpdatedPhases,
        }));
      }
    },
    [projectId, queryClient],
  );

  const updateGantt = useCallback(
    ({
      phaseUpdates,
      taskUpdates,
      sprintUpdates,
      entityId,
    }: {
      phaseUpdates: DateShiftUpdates;
      taskUpdates: DateShiftUpdates;
      sprintUpdates: DateShiftUpdates;
      entityId: number;
    }) => {
      const phaseList = getPhaseList(projectPhases);
      const taskList = getTaskList(tasks, phaseList);
      const sprintList = getSprintList(sprints ?? [], phaseList);
      const taskUpdatesLength = Object.values(taskUpdates).length;
      let timeSpanDates = [...extremeProjectPhaseDates] as [Date, Date];

      if (taskUpdatesLength > THRESHOLD_TASKS_NUMBER) {
        setUpdateDatesDelay(true);
      }

      _setTaskUpdates(taskUpdates);
      setEntityId(entityId);

      Object.values(phaseUpdates)?.forEach(({ startDate, dueDate, id }) => {
        const ganttId = getGanttPhaseId(id);

        const spanDates = updateGanttDates(
          ganttId,
          phaseList[id],
          startDate,
          dueDate,
        );

        timeSpanDates = getExtremeDates(
          timeSpanDates as [Date, Date],
          spanDates as [Date, Date],
        );
      });
      Object.values(taskUpdates)?.forEach(({ startDate, dueDate, id }) => {
        const ganttId = getGanttTaskId(id);

        const spanDates = updateGanttDates(
          ganttId,
          taskList[id],
          startDate,
          dueDate,
        );

        timeSpanDates = getExtremeDates(
          timeSpanDates as [Date, Date],
          spanDates as [Date, Date],
        );
      });
      Object.values(sprintUpdates)?.forEach(({ startDate, dueDate, id }) => {
        const ganttId = getGanttSprintId(id);

        const spanDates = updateGanttDates(
          ganttId,
          sprintList[id],
          startDate,
          dueDate,
        );

        timeSpanDates = getExtremeDates(
          timeSpanDates as [Date, Date],
          spanDates as [Date, Date],
        );
      });

      return timeSpanDates;
    },
    [
      projectPhases,
      tasks,
      sprints,
      extremeProjectPhaseDates,
      updateGanttDates,
      setUpdateDatesDelay,
    ],
  );

  const removeProjectPhaseDependencies = useCallback(
    (phaseId: number, dependentPhaseId: number) => {
      const project = queryClient.getQueryData<Project>(ProjectId(projectId));

      if (project) {
        const { projectPhases: phases } = project;
        let phaseData = phases.find((p) => p.projectPhaseId === phaseId);
        const dependentPhaseData = phases.find(
          (p) => p.projectPhaseId === dependentPhaseId,
        );

        if (phaseData && dependentPhaseData) {
          phaseData = {
            ...phaseData,
            dependentProjectPhases: phaseData.dependentProjectPhases?.filter(
              (p) => p.projectPhaseId !== dependentPhaseId,
            ),
          };

          const updatedPhases = phases.map((p) =>
            p.projectPhaseId === phaseId ? phaseData : p,
          ) as ProjectPhase[];

          if (updatedPhases != null) {
            queryClient.setQueryData<Project>(
              getProjectDetailQueryKey(ProjectId(projectId)),
              () => ({
                ...project,
                projectPhases: updatedPhases,
              }),
            );
          }
        }
      }
    },
    [projectId, queryClient],
  );

  const removeTaskFromDependencies = useCallback(
    (taskId: number, dependentTaskId: number) => {
      const queryTasks = queryClient.getQueryData<Task[]>(
        getProjectTasksQueryKey(ProjectId(projectId)),
      );

      if (queryTasks) {
        let taskData = queryTasks.find((t) => t.taskId === taskId);
        const dependentTaskData = queryTasks?.find(
          (t) => t.taskId === dependentTaskId,
        );

        if (taskData && dependentTaskData) {
          taskData = {
            ...taskData,
            dependentTasks: taskData.dependentTasks?.filter(
              (t) => t.taskId !== dependentTaskId,
            ),
          };

          const updatedTasks = queryTasks.map((t) => {
            if (t.taskId === taskId) {
              return taskData;
            }

            return t;
          });

          queryClient.setQueryData(
            getProjectTasksQueryKey(ProjectId(projectId)),
            updatedTasks,
          );
        }
      }
    },
    [projectId, queryClient],
  );

  const onDrop = useCallback(
    (entity: TaskModel) => {
      const { id: entityId, type: entityType } = entity.get('data');
      const phaseList = getPhaseList(projectPhases);
      const taskList = getTaskList(tasks, phaseList);
      const sprintList = getSprintList(sprints ?? [], phaseList);

      const isTask =
        entityType === GanttType.TaskBar ||
        entityType === GanttType.MilestoneBar;

      const isSprint = entityType === GanttType.Sprint;

      const currentEntity = isTask
        ? taskList[entityId]
        : isSprint
        ? sprintList[entityId]
        : phaseList[entityId];

      const { startDate: currentStartDate, dueDate: currentDueDate } =
        currentEntity;

      const childNodeIds = getChildNodeIds(entityId, {
        ...phaseList,
        ...taskList,
        ...sprintList,
      });
      const childNodes = {};

      childNodeIds.forEach((_id) => {
        childNodes[_id] = taskList[_id] ?? sprintList[_id];
      });

      const duration = getCurrentDuration(
        entityId,
        currentStartDate,
        currentDueDate,
        childNodes,
      );
      const newStartDate = moment(entity.get('startDate'));
      const newDueDate = addDays(newStartDate, duration - 1);

      const [
        phaseUpdates,
        taskUpdates,
        sprintUpdates,
        doesEntityAffectOtherEntities,
      ] = getDateShiftsForEntity(
        phaseList,
        taskList,
        sprintList,
        {
          id: entityId,
          startDate: formatDate(newStartDate),
          dueDate: formatDate(newDueDate),
          type: isTask
            ? EntityType.Task
            : isSprint
            ? EntityType.Sprint
            : EntityType.Phase,
          depth: currentEntity.depth,
        },
        {
          alignPhaseWithTask: alignPhaseDates,
        },
      );

      if (doesEntityAffectOtherEntities && !dependencyModalTimeout) {
        setEntity({
          id: entityId,
          startDate: formatDate(newStartDate),
          dueDate: formatDate(newDueDate),
          type: isTask
            ? EntityType.Task
            : isSprint
            ? EntityType.Sprint
            : EntityType.Phase,
          depth: currentEntity.depth,
          name: currentEntity.name,
        });
        setTaskUpdates(taskUpdates);
        setPhaseUpdates(phaseUpdates);
        setSprintUpdates(sprintUpdates);
        setShowDependencyModal(true);

        return;
      }

      const taskUpdatesLength = Object.values(taskUpdates).length;
      const timeSpanDates = updateGantt({
        phaseUpdates,
        taskUpdates,
        sprintUpdates,
        entityId,
      });

      entity.set('isCalculatedStartDate', false);
      entity.set('isCalculatedEndDate', false);

      if (isTask) {
        dispatch(
          updateTaskTimelineSagaAction({
            projectId,
            taskId: Number(entityId),
            data: {
              startDate: newStartDate.format(API_DATE_FORMAT),
              dueDate: newDueDate.format(API_DATE_FORMAT),
            },
            options: {
              skipStoreUpdate: true,
              skipRefetch: true,
              isSync: taskUpdatesLength > THRESHOLD_TASKS_NUMBER,
              toggleUpdating:
                taskUpdatesLength > THRESHOLD_TASKS_NUMBER &&
                setUpdateDatesDelay,
            },
          }),
        );
      } else if (isSprint) {
        dispatch(
          updateProjectSprintDatesAction({
            data: {
              sprintId: Number(entityId),
              startDate: newStartDate.format(API_DATE_FORMAT),
              dueDate: newDueDate.format(API_DATE_FORMAT),
            },
            options: {
              isSync: taskUpdatesLength > THRESHOLD_TASKS_NUMBER,
              toggleUpdating:
                taskUpdatesLength > THRESHOLD_TASKS_NUMBER &&
                setUpdateDatesDelay,
            },
          }),
        );
      } else {
        dispatch(
          updateProjectPhaseDatesAction({
            data: {
              projectId,
              phaseId: Number(entityId),
              startDate: newStartDate.format(API_DATE_FORMAT),
              dueDate: newDueDate.format(API_DATE_FORMAT),
            },
            options: {
              isSync: taskUpdatesLength > THRESHOLD_TASKS_NUMBER,
              toggleUpdating:
                taskUpdatesLength > THRESHOLD_TASKS_NUMBER &&
                setUpdateDatesDelay,
            },
          }),
        );
        phaseUpdates[entityId] = {
          id: entityId,
          startDate: newStartDate,
          dueDate: newDueDate,
        };
      }

      const filteredUpdates = filterTaskUpdates(taskUpdates, tasks);

      if (isTask) {
        filteredUpdates[entityId] = {
          ...filteredUpdates[entityId],
          startDate: taskUpdates[entityId].startDate,
        };
      }
      updateProjectPhases(Object.values(phaseUpdates));
      updateProjectSprints(
        projectId,
        Object.values(sprintUpdates),
        queryClient,
      );
      updateTasks(Object.values(filteredUpdates));

      dispatch(updateGanttSlackAction({ projectId }));

      ganttRef.instance.setTimeSpan(timeSpanDates[0], timeSpanDates[1]);

      // ganttRef?.instance.scrollToDate(new Date(newStartDate.toString()));
    },
    [
      projectPhases,
      tasks,
      sprints,
      alignPhaseDates,
      dependencyModalTimeout,
      updateGantt,
      dispatch,
      updateProjectPhases,
      projectId,
      queryClient,
      updateTasks,
      ganttRef,
      setEntity,
      setTaskUpdates,
      setPhaseUpdates,
      setSprintUpdates,
      setShowDependencyModal,
      setUpdateDatesDelay,
    ],
  );

  /**
   * only tasks can be resized and NOT the phases
   * (as per our implementation, not limitation from Bryntum)
   * hence the following handler works assuming task is resized.
   */
  const onResize = useCallback(
    (entity: TaskModel) => {
      const { id: entityId, type: entityType } = entity.get('data');
      const phaseList = getPhaseList(projectPhases);
      const taskList = getTaskList(tasks, phaseList);
      const sprintList = getSprintList(sprints ?? [], phaseList);
      const isTask =
        entityType === GanttType.TaskBar ||
        entityType === GanttType.MilestoneBar;
      const newStartDate = moment(entity.get('startDate'));

      let newDueDate = moment(entity.get('endDate'));

      if (formatDate(newDueDate) !== taskresizeEndDate.current) {
        newDueDate = subtractDays(formatDate(newDueDate), 1);
      }

      const [
        phaseUpdates,
        taskUpdates,
        sprintUpdates,
        doesEntityAffectOtherEntities,
      ] = getDateShiftsForEntity(
        phaseList,
        taskList,
        sprintList,
        {
          id: entityId,
          dueDate: formatDate(newDueDate),
          type: isTask ? EntityType.Task : EntityType.Phase,
          depth: taskList[entityId].depth,
        },
        {
          alignPhaseWithTask: alignPhaseDates,
        },
      );

      if (doesEntityAffectOtherEntities && !dependencyModalTimeout) {
        setEntity({
          id: entityId,
          dueDate: formatDate(newDueDate),
          type: isTask ? EntityType.Task : EntityType.Phase,
          depth: taskList[entityId].depth,
          name: taskList[entityId].name,
        });
        setTaskUpdates(taskUpdates);
        setPhaseUpdates(phaseUpdates);
        setSprintUpdates(sprintUpdates);
        setShowDependencyModal(true);

        return;
      }

      const taskUpdatesLength = Object.values(taskUpdates).length;

      const timeSpanDates = updateGantt({
        phaseUpdates,
        taskUpdates,
        sprintUpdates,
        entityId,
      });

      entity.set('isCalculatedStartDate', false);
      entity.set('isCalculatedEndDate', false);

      dispatch(
        updateTaskTimelineSagaAction({
          projectId,
          taskId: Number(entityId),
          data: {
            startDate: newStartDate.format(API_DATE_FORMAT),
            dueDate: newDueDate.format(API_DATE_FORMAT),
          },
          options: {
            skipStoreUpdate: true,
            skipRefetch: true,
            isSync: taskUpdatesLength > THRESHOLD_TASKS_NUMBER,
            toggleUpdating:
              taskUpdatesLength > THRESHOLD_TASKS_NUMBER && setUpdateDatesDelay,
          },
        }),
      );

      const filteredUpdates = filterTaskUpdates(taskUpdates, tasks);

      filteredUpdates[entityId] = {
        ...filteredUpdates[entityId],
        dueDate: taskUpdates[entityId].dueDate,
      };
      updateProjectPhases(Object.values(phaseUpdates));
      updateProjectSprints(
        projectId,
        Object.values(sprintUpdates),
        queryClient,
      );
      updateTasks(Object.values(taskUpdates));

      dispatch(updateGanttSlackAction({ projectId }));
      ganttRef.instance.setTimeSpan(timeSpanDates[0], timeSpanDates[1]);

      // ganttRef?.instance.scrollToDate(new Date(newDueDate.toString()));
    },
    [
      projectPhases,
      tasks,
      sprints,
      alignPhaseDates,
      dependencyModalTimeout,
      updateGantt,
      dispatch,
      projectId,
      setUpdateDatesDelay,
      updateProjectPhases,
      queryClient,
      updateTasks,
      ganttRef,
      setEntity,
      setTaskUpdates,
      setPhaseUpdates,
      setSprintUpdates,
      setShowDependencyModal,
    ],
  );

  const onDependencyCreate = useCallback(
    ({ source, target }: { source: TaskModel; target: TaskModel }) => {
      const phaseList = getPhaseList(projectPhases);
      const taskList = getTaskList(tasks, phaseList);
      const sprintList = getSprintList(sprints ?? [], phaseList);
      const { id: targetEntityId } = target.get('data');
      const { id: sourceEntityId, type: sourceEntityType } = source.get('data');
      const { taskStore } = ganttRef?.instance;

      const sourceGanttId = `${GanttType.TaskBar}-${sourceEntityId}`;
      const sourceRecord = taskStore.getById(sourceGanttId);

      const targetGanttId = `${GanttType.TaskBar}-${targetEntityId}`;
      const targetRecord = taskStore.getById(targetGanttId);

      const isTask =
        sourceEntityType === GanttType.TaskBar ||
        sourceEntityType === GanttType.MilestoneBar;

      const sourceEntity = isTask
        ? taskList[sourceEntityId]
        : phaseList[sourceEntityId];

      const [phaseUpdates, taskUpdates, sprintUpdates] = getDateShiftsForEntity(
        phaseList,
        taskList,
        sprintList,
        {
          id: sourceEntityId,
          dependencyFor: [
            ...(sourceEntity.dependencyFor || []),
            targetEntityId,
          ],
          type: isTask ? EntityType.Task : EntityType.Phase,
          depth: sourceEntity.depth,
        },
        {
          alignPhaseWithTask: alignPhaseDates,
        },
      );

      const taskUpdatesLength = Object.values(taskUpdates).length;

      const timeSpanDates = updateGantt({
        phaseUpdates,
        taskUpdates,
        sprintUpdates,
        entityId: sourceEntityId,
      });

      if (isTask) {
        const shouldHandleNullDates =
          targetRecord.get('isCalculatedStartDate') ||
          targetRecord.get('isCalculatedEndDate') ||
          sourceRecord.get('isCalculatedStartDate') ||
          sourceRecord.get('isCalculatedEndDate');

        dispatch(
          createDependencySagaAction({
            projectId,
            taskId: Number(targetEntityId),
            dependentTaskId: Number(sourceEntityId),
            canAssignDates: true,
            taskUpdates,
            isSync: taskUpdatesLength > THRESHOLD_TASKS_NUMBER,
            toggleUpdating:
              taskUpdatesLength > THRESHOLD_TASKS_NUMBER && setUpdateDatesDelay,
            handleNullDates: shouldHandleNullDates,
          }),
        );
      } else {
        createPhaseDependency({
          phaseId: targetEntityId,
          dependentPhaseId: sourceEntityId,
          isSync: taskUpdatesLength > THRESHOLD_TASKS_NUMBER,
          toggleUpdating:
            taskUpdatesLength > THRESHOLD_TASKS_NUMBER && setUpdateDatesDelay,
        });
      }

      updateProjectPhases(Object.values(phaseUpdates));
      updateProjectSprints(
        projectId,
        Object.values(sprintUpdates),
        queryClient,
      );
      updateTasks(Object.values(taskUpdates));

      dispatch(updateGanttSlackAction({ projectId }));

      ganttRef.instance.setTimeSpan(timeSpanDates[0], timeSpanDates[1]);

      // ganttRef?.instance.scrollToDate(
      //   new Date(sourceEntity.dueDate?.toString() as string),
      // );
    },
    [
      projectPhases,
      tasks,
      sprints,
      ganttRef,
      alignPhaseDates,
      updateGantt,
      dispatch,
      updateProjectPhases,
      projectId,
      queryClient,
      updateTasks,
      setUpdateDatesDelay,
      createPhaseDependency,
    ],
  );

  const onDependencyDelete = useCallback(
    async ({ source, target }: { source: TaskModel; target: TaskModel }) => {
      const { id: targetEntityId } = target.get('data');
      const { id: sourceEntityId, type: sourceEntityType } = source.get('data');
      const isTask =
        sourceEntityType === GanttType.TaskBar ||
        sourceEntityType === GanttType.MilestoneBar;

      if (isTask) {
        await TaskAPI.removeDependentTask(projectId, targetEntityId, {
          dependentTaskIds: [sourceEntityId],
        });

        await queryClient.invalidateQueries(
          getProjectDependencyMapKey(projectId),
        );

        dispatch(
          dependencyRemovedAction({
            taskId: targetEntityId,
            dependentTaskId: sourceEntityId,
            canAssignDates: false,
          }),
        );
        removeTaskFromDependencies(targetEntityId, sourceEntityId);
      } else {
        await deletePhaseDependency({
          phaseId: targetEntityId,
          dependentPhaseId: sourceEntityId,
        });
        removeProjectPhaseDependencies(targetEntityId, sourceEntityId);
      }

      dispatch(updateGanttSlackAction({ projectId }));
    },
    [
      deletePhaseDependency,
      dispatch,
      projectId,
      queryClient,
      removeProjectPhaseDependencies,
      removeTaskFromDependencies,
    ],
  );

  const openTaskPane = useCallback(
    (record, cellElement: HTMLElement, target: HTMLElement) => {
      if (
        target.classList.contains('b-tree-expander') ||
        target.className.includes('controlled-popover-action') ||
        target.className.includes('ant-avatar-group') ||
        target.closest('.b-tree-expander') ||
        target.className.includes('b-grid-cell') ||
        validNodeNames.includes(target.nodeName) ||
        (target.nodeName === 'SPAN' &&
          !target.className.includes('task-detail')) ||
        target.className.includes('custom-cell-renderer') ||
        target.closest('.custom-cell-renderer')
      ) {
        return;
      }

      if (record.get('type') === GanttType.TaskBar) {
        navigateToTask(TaskId(projectId, record.get('data').id));
      } else if (record.get('type') === GanttType.Sprint) {
        const data = record.get('data');

        push(`/projects/${projectId}/plan/timeline/sprints/${data.id}`);
      }
    },
    [navigateToTask, projectId, push],
  );

  useEffect(() => {
    switch (event?.name) {
      case 'onDrop':
        onDrop(event.payload as TaskModel);
        break;
      case 'onResize':
        onResize(event.payload as TaskModel);
        break;
      case 'onDependencyCreate':
        onDependencyCreate(event.payload as CreateDependencyEventPayload);
        break;
      case 'onDependencyDelete':
        onDependencyDelete(event.payload as CreateDependencyEventPayload);
        break;
      default:
        break;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [event]);
  const projectResource = useProjectAsRBACResource(projectId);
  const { hasAccessToResource } = useWithAccess();

  return useMemo(
    () => ({
      listeners: {
        taskDrop: ({ taskRecords }: { taskRecords: TaskModel[] }): void => {
          setEvent({
            name: 'onDrop',
            payload: taskRecords[0],
          });
        },
        taskPartialResize: ({ taskRecord }) => {
          taskresizeEndDate.current = formatDate(taskRecord.get('endDate'));
        },
        taskResizeEnd: ({ taskRecord }: { taskRecord: TaskModel }): void => {
          setEvent({
            name: 'onResize',
            payload: taskRecord,
          });
        },
        afterDependencyCreateDrop: ({
          data: { source, target, valid },
        }: {
          data: {
            source: TaskModel;
            target: TaskModel;
            valid: boolean;
          };
        }) => {
          if (!valid) {
            return;
          }
          setEvent({
            name: 'onDependencyCreate',
            payload: { source, target },
          });
        },

        dependencyClick: ({ dependency }) => {
          if (
            !hasAccessToResource({
              rule: 'TASK_DEPENDENCY:WRITE',
              resourceData: projectResource,
            })
          ) {
            return;
          }
          confirm({
            title: translate('timeline.dependency.delete.confirm.title'),
            content: translate('timeline.dependency.delete.confirm.content', {
              from: dependency.fromEvent.name,
              to: dependency.toEvent.name,
              b: (chunks) => <b>{chunks}</b>,
            }),
            onOk: () => {
              dependency.remove();
              setEvent({
                name: 'onDependencyDelete',
                payload: {
                  source: dependency.fromEvent,
                  target: dependency.toEvent,
                },
              });
            },
          });
        },

        cellClick({
          cellElement,
          record,
          target,
        }: {
          cellElement: HTMLElement;
          record: TaskModel;
          target: HTMLElement;
        }) {
          return openTaskPane(record, cellElement, target);
        },
      },
    }),
    [hasAccessToResource, openTaskPane, projectResource, translate],
  );
};
